import { PLANNING_APP_NAME } from '@/lib/constants/app.constants';
import { apiRequest, ApiError } from '@/services/api/client';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { systemConfig } from '@/config/system.config';
import { AuthenticationError, NetworkError } from '@/lib/errors/errorTypes';
import { logger } from '@/lib/logger';
import type { LoginResponse } from './login-result.interface';

const AUTH_LOG = '[planning-auth]';

interface LoginRequestBody {
  token?: string;
  tenantId: string;
  appName: typeof PLANNING_APP_NAME;
}

/**
 * Exchange B2C id_token for application JWT using the build-time Pepkor tenant.
 */
export async function login(idToken: string): Promise<LoginResponse> {
  try {
    const body: LoginRequestBody = {
      token: idToken,
      tenantId: systemConfig.tenantId,
      appName: PLANNING_APP_NAME,
    };
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'login(): POST /auth/login (B2C id_token exchange)', {
      tenantId: systemConfig.tenantId,
      appName: PLANNING_APP_NAME,
      idTokenLength: idToken.length,
    });
    const { data } = await apiRequest<LoginResponse>({
      url: API_ENDPOINTS.auth.login,
      method: 'POST',
      body,
      skipAuth: true,
    });
    if (!data?.accessToken || !data.user || !data.tenantId) {
      logger.error('Invalid login response: missing accessToken, user, or tenantId');
      throw new AuthenticationError('Invalid login response. Please try signing in again.');
    }
    return data;
  } catch (error) {
    return handleLoginError(error);
  }
}

function handleLoginError(error: unknown): never {
  if (error instanceof ApiError) {
    logger.error('Login API error', error instanceof Error ? error : new Error(String(error)));
    if (error.status === 401 || error.status === 403) {
      const message =
        error.getDisplayMessage() || 'Authentication failed. Please try signing in again.';
      const details = error.data ? (error.data as Record<string, unknown>) : undefined;
      throw new AuthenticationError(message, details);
    }
    const message = error.getDisplayMessage() || 'Failed to sign in. Please try again.';
    throw new ApiError(message, error.status, error.data);
  }
  if (error instanceof TypeError && error.message.includes('fetch')) {
    logger.error('Network error during login', error);
    throw new NetworkError(
      'Unable to connect to the server. Please check your internet connection and try again.'
    );
  }
  if (error instanceof AuthenticationError || error instanceof NetworkError) {
    throw error;
  }
  const err = error instanceof Error ? error : new Error(String(error));
  logger.error('Unexpected login error', err);
  throw new AuthenticationError('An unexpected error occurred during sign in. Please try again.');
}

/**
 * Builds the Azure AD B2C login URL with redirect_uri from injected system config.
 */
export function getB2CLoginUrl(): string | null {
  try {
    const loginUrl = systemConfig.getB2CLoginUrl();
    if (!loginUrl) {
      logger.error('B2C login URL is not configured');
      return null;
    }
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'getB2CLoginUrl(): ready');
    return loginUrl;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('Unexpected error building B2C login URL', err);
    throw new Error('Failed to load login configuration. Please refresh the page and try again.');
  }
}

/**
 * If the browser has a valid SSO cookie from another app, exchange it for a planning session.
 */
export async function trySsoSilentLogin(): Promise<LoginResponse | null> {
  try {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'trySsoSilentLogin(): POST /auth/login (SSO cookie only)', {
      tenantId: systemConfig.tenantId,
      appName: PLANNING_APP_NAME,
    });
    const { data } = await apiRequest<LoginResponse>({
      url: API_ENDPOINTS.auth.login,
      method: 'POST',
      body: {
        tenantId: systemConfig.tenantId,
        appName: PLANNING_APP_NAME,
      },
      skipAuth: true,
    });
    if (data?.accessToken && data.user && data.tenantId) {
      return data;
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'trySsoSilentLogin(): failed (no SSO session or network)', err);
    return null;
  }
}

/**
 * Logout from the application.
 */
export async function logout(): Promise<void> {
  try {
    await apiRequest({
      url: API_ENDPOINTS.auth.logout,
      method: 'GET',
    });
  } catch (error) {
    if (error instanceof ApiError) {
      logger.warn('Logout API call failed', { status: error.status, message: error.message });
    } else {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Unexpected logout error', err);
    }
  }
}
